import { useContext, useEffect, useRef, useState } from "react";
import { GlobalContext } from "../../Context/GlobalContext";
import { useInfiniteHits, useInstantSearch } from "react-instantsearch";
import { isValidArray } from "../../Layout/Common/common";
import { getUniqueFamiliesByLowestPrice, hasActiveRefinements } from "../../Layout/Common/algoliaInstantLib";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import { ProductCardSkeleton } from "../../Skeleton/productCardSkeleton";
import { ProductListSkeleton } from "../../Skeleton/ProductListSkeleton";
import { ProductCardAlgoliaList } from "../../Layout/Common/productCardAlgoliaList";
import { ProductCardAlgolia } from "../../Layout/Common/productCardAlgolia";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../Context/AuthContext";
import { DynamicLoader } from "./dynamicLoader";

// distinctFamilyMode: on "grouped result" pages (brand / brand-model) the index is served with
// distinct=true while browsing, so a hit's own details.productCount (not the group size) tells us
// whether it's a family. Once a facet filter is applied (distinct=false) we fall back to normal
// group-size grouping. Off by default, so search/home pages keep their existing behaviour.
export const CustomInfiniteHitsLite = ({ catFilter, distinctFamilyMode = false }) => {
  const location = useLocation();
  const { isGridView } = useContext(GlobalContext);
  const { priceCode } = useAuth();
  const { currentPageHits, showMore, results, isLastPage, isFirstPage } = useInfiniteHits();
  // Real Algolia request state — 'loading'/'stalled' means a fetch is in flight. Used so we show the
  // loader (not "No Results Found") until the search actually settles.
  const { status, indexUiState } = useInstantSearch();
  const isSearching = status === "loading" || status === "stalled";

  const [productList, setProductList] = useState(null);
  const [currentCategory, setcurrentCategory] = useState(null);
  const currentPageRef = useRef(0);
  const loadedPagesRef = useRef(new Set());
  const [initLoad, setInitLoad] = useState(true);

  useEffect(() => {
    var currentCategoryFilter = results?._state?.hierarchicalFacetsRefinements?.["categories.lvl0"];
    if (isValidArray(currentCategoryFilter)) setcurrentCategory(currentCategoryFilter[0]);
    else if (catFilter) {
      setcurrentCategory(null);
      setProductList(null);
    }
  }, [results, catFilter]);

  useEffect(() => {
    if (isValidArray(currentPageHits) && (currentCategory || catFilter == false)) {
      // Whole breadcrumb, not [0] - see searchResultItems.jsx for why.
      const activeCategory = indexUiState?.hierarchicalMenu?.["categories.lvl0"] ?? null;
      const hasRefinements = hasActiveRefinements(indexUiState);
      var finalProducts = getUniqueFamiliesByLowestPrice(
        currentPageHits,
        priceCode,
        distinctFamilyMode ? activeCategory : undefined,
        { familyByProductCount: distinctFamilyMode && !hasRefinements },
      );
      if (results.page > 0) {
        if (loadedPagesRef.current.has(results.page)) return;
        loadedPagesRef.current.add(results.page);
        currentPageRef.current = results.page;
        setProductList((prev) => [...(prev ?? []), ...finalProducts]);
      } else if (results.page == 0) {
        loadedPagesRef.current = new Set([0]);
        currentPageRef.current = results.page;
        setProductList(finalProducts);
      }
      if (results.query != "") {
        setInitLoad(false);
      }
    } else {
      if (window.location.pathname.includes("/search") && results.query != "") setInitLoad(false);
      else if (window.location.pathname.includes("/search") == false) setInitLoad(false);
      // Only empty the grid once the search has SETTLED with genuinely no hits. During an in-flight
      // fetch keep the previous results mounted, so the content height (and the footer) doesn't
      // collapse and bounce up/down while loading.
      if (status === "idle" && results?.nbHits === 0) setProductList(null);
    }
  }, [results.page, results.query, results.index, results.nbHits, currentCategory, currentPageHits, status]);

  return (
    // Reserve vertical space so the results area never collapses to near-zero height while loading —
    // that collapse is what makes the page (and the footer) jump up/down during a fetch.
    <div className="results-min-height">
      {isValidArray(productList) && !initLoad ? (
        isGridView ? (
          <TransitionGroup className={"row row-spacing row-equal mt-3 smooth-fade-in"}>
            <div className={"col-lg-12"}>
              <div className={"productCard-wrapper"}>
                {productList.map((hit, index) => (
                  <CSSTransition key={hit.key} timeout={500} classNames="fade">
                    <div data-view={isGridView} className={"productCard"} data-insights-object-id={hit.productId}>
                      <ProductCardAlgolia data={hit} catFilter={catFilter} />
                    </div>
                  </CSSTransition>
                ))}
              </div>
              {/* {isLastPage == false && (
                <DynamicLoader isGridView={isGridView} isLastPage={isLastPage} showMore={showMore} />
              )} */}
            </div>
          </TransitionGroup>
        ) : (
          <>
            <TransitionGroup className={"col-sm-12 col-md-12 col-lg-12 mt-3 mt-lg-3 smooth-fade-in"}>
              {productList.map((hit, index) => (
                <CSSTransition key={hit.key} timeout={500} classNames="fade">
                  <div
                    data-view={isGridView}
                    className={"search-row rowProducts-list position-relative"}
                    data-insights-object-id={hit.productId}
                  >
                    <ProductCardAlgoliaList data={hit} index={hit.productId} />
                  </div>
                </CSSTransition>
              ))}
            </TransitionGroup>
            {/* {isLastPage == false && (
              <DynamicLoader isGridView={isGridView} isLastPage={isLastPage} showMore={showMore} />
            )} */}
          </>
        )
      ) : initLoad || isSearching || results?.nbHits > 0 ? (
        // Still fetching (or Algolia reports hits we haven't finished processing) — show a FULL skeleton
        // grid so the area keeps a realistic height and "No Results Found" never flashes mid-fetch.
        isGridView ? (
          <div className="col-lg-12 mt-3">
            <div className="productCard-wrapper">
              {Array.from({ length: 12 }).map((_, i) => (
                <div className="productCard" key={"sk-" + i}>
                  <ProductCardSkeleton />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="col-lg-12 mt-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <ProductListSkeleton key={"sk-" + i} />
            ))}
          </div>
        )
      ) : (
        <div className="no-results-found text-center w-100 py-5 px-3" style={{ margin: "auto" }}>
          <i className="fa-solid fa-magnifying-glass mb-3" style={{ fontSize: "3rem", color: "#adb5bd" }} />
          <div style={{ fontSize: "1.1rem", color: "#6c757d" }}>No Results Found</div>
        </div>
      )}
    </div>
  );
};
